import { useState, useRef, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Popper,
  ClickAwayListener,
  Paper,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckIcon from "@mui/icons-material/Check";
import { useSetActionCall } from "../modules/services";

export default function ColorSelector({ onChange, productData, sessionId }) {
  const { mutate: variantChange } = useSetActionCall();
  const productKey = productData?.[0]?.product_key;
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const colorData = useMemo(() => {
    const colorProperty = productData?.[0]?.property?.find(
      (prop) => prop.property_name.toLowerCase() === "color"
    );
    if (!colorProperty) return { variants: [], defaultVariant: null };

    const variants = colorProperty?.variants?.map((variant) => {
      const icon = variant.variant_icons.find((i) => i.file_type === "M");
      console.log("isn", variant);
      return {
        id: variant.variant_id,
        name: variant.display_name,
        image: icon?.path || "",
        isDefault: Boolean(variant.is_default),
        property_id: colorProperty.property_id,
      };
    });

    const defaultVariant =
      variants.find((v) => v.isDefault) || variants[0] || null;
    return { variants, defaultVariant };
  }, [productData]);

  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (colorData.defaultVariant) {
      setSelected(colorData.defaultVariant);
    }
  }, [colorData.defaultVariant]);

  useEffect(() => {
    if (selected && selected.id && colorData.variants.length) {
      variantChange({
        session_id: sessionId,
        message: {
          type: "change_variant",
          message: {
            product_key: productKey,
            property_id: selected.property_id,
            variant_id: selected.id,
          },
        },
      });
    }
  }, [selected, sessionId, productKey, variantChange]);

  const handleSelect = (variant) => {
    setSelected(variant);
    onChange?.(variant);
    setOpen(false);

    if (variant.id && variant.property_id) {
      variantChange({
        session_id: sessionId,
        message: {
          type: "change_variant",
          message: {
            product_key: productKey,
            property_id: variant.property_id,
            variant_id: variant.id,
          },
        },
      });
    }
  };

  if (!colorData.variants.length) return null;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box>
        <Box
          ref={anchorRef}
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: "6px",
            width: "fit-content",
            bgcolor: "#fff",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: "0.875rem", color: "text.primary" }}
          >
            {selected?.name || "Select Color"}
          </Typography>

          <Box
            component="img"
            src={selected?.image || ""}
            alt={selected?.name || "color swatch"}
            onError={(e) => {
              e.currentTarget.src = "";
              e.currentTarget.style.backgroundColor = "#f0f0f0";
            }}
            sx={{
              width: 20,
              height: 20,
              borderRadius: "4px",
              border: "1px solid #ccc",
              objectFit: "cover",
              backgroundColor: selected?.image ? "transparent" : "#f0f0f0",
            }}
          />

          <IconButton size="small" sx={{ p: 0 }}>
            {open ? (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>

        {/* Dropdown */}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          disablePortal
          sx={{ zIndex: 1300, mt: 1 }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 2,
              display: "grid",
              gridTemplateColumns: "repeat(4, 36px)",
              gap: 1,
              bgcolor: "#fff",
              borderRadius: 1,
            }}
          >
            {colorData.variants.map((variant) => {
              const isSelected = selected?.id === variant.id;
              return (
                <Box
                  key={variant.id}
                  onClick={() => handleSelect(variant)}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    border: isSelected ? "2px solid #000" : "1px solid #ccc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    position: "relative",
                    backgroundColor: "#fff",
                  }}
                >
                  <Box
                    component="img"
                    src={variant.image}
                    alt={variant.name}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      objectFit: "cover",
                    }}
                  />
                  {isSelected && (
                    <CheckIcon
                      sx={{
                        fontSize: 18,
                        color: "#000",
                        position: "absolute",
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
